import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';

// Store
import { useStore } from './store/useStore';

// Hooks
import { useHydration } from './hooks/useHydration';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

// Pages
import Home from './pages/Home';
import WineShop from './pages/WineShop';
import WineDetail from './pages/WineDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';

// Legal Pages
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCategories from './pages/admin/AdminCategories';
import AdminWineries from './pages/admin/AdminWineries';
import AdminTypes from './pages/admin/AdminTypes';
import AdminWines from './pages/admin/AdminWines';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminShipping from './pages/admin/AdminShipping';
import AdminDiscounts from './pages/admin/AdminDiscounts';
import AdminSections from './pages/admin/AdminSections';
import FirebaseManager from './components/admin/FirebaseManager';

// Services
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Firebase setup (development only)
if (process.env.NODE_ENV === 'development') {
  import('./scripts/setupFirebaseCollections').then(module => {
    window.setupFirebase = module.setupFirebaseCollections;
    window.clearFirebase = module.clearFirebaseCollections;
    console.log('🔥 Firebase setup available:');
    console.log('- window.setupFirebase() - Configure collections');
    console.log('- window.clearFirebase() - Clear all collections');
  });

  // Admin setup script
  import('./scripts/setupAdminUser').then(module => {
    window.setupAdminUser = module.setupAdminUser;
    window.setupAdminFromConsole = module.setupAdminFromConsole;
    console.log('👑 Admin setup available:');
    console.log('- window.setupAdminUser(email, password) - Setup admin user');
    console.log('- window.setupAdminFromConsole() - Interactive admin setup');
  });

  // Firebase debugging tools
  import('./services/firebaseService').then(module => {
    window.debugFirebaseOrders = module.debugFirebaseOrders;
    window.cleanupTestOrders = module.cleanupTestOrders;
    window.firebaseOrdersService = module.firebaseOrdersService;
    console.log('🧪 Firebase debug tools available:');
    console.log('- window.debugFirebaseOrders() - Test orders functionality');
    console.log('- window.cleanupTestOrders() - Clean test orders');
    console.log('- window.firebaseOrdersService - Direct access to orders service');
  });

  // Inicializar servicios de envío y descuentos
  import('./services/firebaseShippingService').then(module => {
    module.initializeShippingRates();
    window.firebaseShippingService = module.firebaseShippingService;
    console.log('🚚 Shipping service initialized');
  });

  import('./services/firebaseDiscountService').then(module => {
    module.initializeDiscountCodes();
    window.firebaseDiscountService = module.firebaseDiscountService;
    console.log('🎫 Discount service initialized');
  });

  // Inicializar servicio de secciones
  import('./services/firebaseSectionService').then(module => {
    window.firebaseSectionService = module.firebaseSectionService;
    window.initializeSectionData = module.initializeSectionData;
    console.log('📋 Section service initialized');
  });
}

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, user } = useStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route Component (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { user } = useStore();
  const isAuthenticated = !!user;

  // Si está autenticado, redirigir según el rol
  if (isAuthenticated) {
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { setUser, setLoading, isLoading } = useStore();
  const hydrated = useHydration();

  // Estado para control de edad
  const [ageVerified, setAgeVerified] = useState(() => {
    return localStorage.getItem('ageVerified') === 'true';
  });
  const [openAgeDialog, setOpenAgeDialog] = useState(!ageVerified);

  useEffect(() => {
    if (!ageVerified) {
      setOpenAgeDialog(true);
    }
  }, [ageVerified]);

  const handleAcceptAge = () => {
    localStorage.setItem('ageVerified', 'true');
    setAgeVerified(true);
    setOpenAgeDialog(false);
  };

  const handleRejectAge = () => {
    window.location.href = 'https://www.google.com';
  };

  useEffect(() => {
    if (!hydrated) return;
    const auth = getAuth();
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Obtener información adicional del usuario desde Firestore
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('./config/firebase');
          
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          let userRole = 'user'; // Rol por defecto
          let userName = firebaseUser.displayName;
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userRole = userData.role || 'user';
            userName = userData.name || firebaseUser.displayName;
          }
          
          const userWithRole = {
            uid: firebaseUser.uid,
            name: userName,
            email: firebaseUser.email || null,
            role: userRole,
            isAnonymous: firebaseUser.isAnonymous
          };
          
          setUser(userWithRole);
        } catch (error) {
          console.error('Error obteniendo datos de usuario:', error);
          // Si falla Firestore, usar datos básicos
          setUser({
            uid: firebaseUser.uid,
            name: firebaseUser.displayName,
            email: firebaseUser.email || null,
            role: 'user',
            isAnonymous: firebaseUser.isAnonymous
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [hydrated, setUser, setLoading]);

  // Mostrar loading mientras se hidrata el store o se está cargando
  if (!hydrated || isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh' 
    }}>
      <Dialog open={openAgeDialog} disableEscapeKeyDown disableBackdropClick fullWidth maxWidth="xs">
        <DialogTitle>Verificación de edad</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <p>
              El acceso a este sitio web está restringido a personas mayores de edad.<br />
              Por favor, confirma que tienes al menos 18 años para continuar navegando.<br /><br />
              Si no cumples con este requisito, lamentablemente no podemos permitirte el acceso.
            </p>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button variant="contained" color="primary" onClick={handleAcceptAge}>
            Sí, soy mayor de edad
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleRejectAge}>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <Navbar />
      
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/tienda" element={<WineShop />} />
          <Route path="/vino/:id" element={<WineDetail />} />
          <Route path="/cart" element={<Cart />} />
          
          {/* Legal Pages */}
          <Route path="/privacidad" element={<Privacy />} />
          <Route path="/terminos" element={<Terms />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />

          {/* Checkout - Public Route (no login required) */}
          <Route path="/checkout" element={<Checkout />} />

          {/* Protected Routes */}
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          
          <Route path="/orders" element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          } />
          
          <Route path="/order/:id" element={
            <ProtectedRoute>
              <OrderDetail />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/categories" element={
            <ProtectedRoute adminOnly>
              <AdminCategories />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/wineries" element={
            <ProtectedRoute adminOnly>
              <AdminWineries />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/types" element={
            <ProtectedRoute adminOnly>
              <AdminTypes />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/wines" element={
            <ProtectedRoute adminOnly>
              <AdminWines />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/orders" element={
            <ProtectedRoute adminOnly>
              <AdminOrders />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute adminOnly>
              <AdminUsers />
            </ProtectedRoute>
          } />

          <Route path="/admin/shipping" element={
            <ProtectedRoute adminOnly>
              <AdminShipping />
            </ProtectedRoute>
          } />

          <Route path="/admin/discounts" element={
            <ProtectedRoute adminOnly>
              <AdminDiscounts />
            </ProtectedRoute>
          } />

          <Route path="/admin/sections" element={
            <ProtectedRoute adminOnly>
              <AdminSections />
            </ProtectedRoute>
          } />

          <Route path="/admin/firebase" element={
            <ProtectedRoute adminOnly>
              <FirebaseManager />
            </ProtectedRoute>
          } />

          {/* 404 Route */}
          <Route path="*" element={
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '60vh'
            }}>
              <div style={{ textAlign: 'center' }}>
                <h1>404 - Página no encontrada</h1>
                <p>La página que buscas no existe.</p>
              </div>
            </Box>
          } />
        </Routes>
      </Box>

      <Footer />
      <LoadingSpinner />
    </Box>
  );
}

export default App; 